import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { useMadFormatter, useTripBookingAdapter } from "../../config";
import { useMoroccoToday } from "../../hooks/use-trip-booking";
import { formatMad } from "../../internal/format";
import { dayBookingPlan, isPastDay, type OfferLookup } from "../../lib/trip-booking";
import { DEFAULT_TRIP_PEOPLE } from "../../state/trip-storage";
import { partyLabel, partyOf } from "../../lib/trip-party";
import type { DiscoverItem, Trip, TripDay } from "../../types";
import { dayIso, tripStartOf } from "./trip-date";

type SendState = "idle" | "sending" | "sent" | "failed";

/** How long "Added" shows before the bucket opens (the send beat). */
const SENT_BEAT_MS = 650;

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

/**
 * The foot of a day: what is due on the day for the party, and one button
 * that sends the day's activities to the bucket, dated and timed.
 *
 * **Never a payment.** Activities are paid on arrival, and that is the only
 * money this block names. Restaurants are free tables the bucket cannot hold
 * and stays are paid online from their own page, so both are left out and
 * named underneath rather than quietly dropped.
 *
 * The amount is the bucket's own: once every activity's packages have loaded
 * it is the sum of the lines the button will add, each priced by `calcTotal`.
 * Until then it is the catalogue's lead price times the party, prefixed
 * "from", with a line saying the exact total comes at checkout.
 */
export function TripDayBooking({
  trip,
  day,
  dayIndex,
  resolveItem,
  offerFor,
  onChangeWhen,
}: {
  trip: Trip;
  day: TripDay;
  dayIndex: number;
  resolveItem: (itemId: string) => DiscoverItem | null;
  offerFor: OfferLookup | undefined;
  onChangeWhen: () => void;
}) {
  const { t, i18n } = useTranslation();
  const adapter = useTripBookingAdapter();
  const format = useMadFormatter();
  const today = useMoroccoToday();
  const [state, setState] = useState<SendState>("idle");
  const rootRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);

  const party = partyOf(trip, DEFAULT_TRIP_PEOPLE);
  const date = dayIso(tripStartOf(trip), dayIndex);
  const plan = dayBookingPlan(day, resolveItem, party, date, offerFor);
  if (plan.activities.length === 0 && plan.tables.length === 0 && plan.stays.length === 0) {
    return null;
  }

  const money = (mad: number) => (format ? format(mad) : formatMad(mad, i18n.language));
  const past = today !== null && isPastDay(date, today);
  const count = plan.lines.length;
  const canBook =
    adapter !== null && !past && !plan.loading && count > 0 && state !== "sending" && state !== "sent";

  const shake = () => {
    const el = rootRef.current;
    if (!el || prefersReducedMotion() || typeof el.animate !== "function") return;
    el.animate(
      [
        { transform: "translateX(0)" },
        { transform: "translateX(-6px)" },
        { transform: "translateX(5px)" },
        { transform: "translateX(-3px)" },
        { transform: "translateX(0)" },
      ],
      { duration: 300, easing: "ease-out" },
    );
  };

  const book = async () => {
    if (!adapter || !canBook) return;
    setState("sending");
    try {
      const added = await adapter.addToBucket(plan.lines);
      if (added <= 0) throw new Error("nothing added");
      setState("sent");
      timerRef.current = window.setTimeout(() => adapter.openBucket(), SENT_BEAT_MS);
    } catch {
      setState("failed");
      shake();
    }
  };

  const names = (items: readonly DiscoverItem[]) => items.map((i) => i.title).join(", ");

  return (
    <div className="mj-trip-book" ref={rootRef} data-testid="trip-day-booking">
      {plan.activities.length > 0 && (
        <div className="mj-trip-book__due">
          <span>
            {party.childAges.length > 0
              ? t("trip.book.due_party", { party: partyLabel(t, party) })
              : t("trip.book.due_people", { count: party.adults })}
          </span>
          <strong>
            {plan.total > 0
              ? plan.exact
                ? money(plan.total)
                : t("trip.price.from", { price: money(plan.total) })
              : null}
          </strong>
        </div>
      )}

      {plan.activities.length > 0 && adapter && !past && (
        <button
          type="button"
          className="mj-trip-book__cta mj-press"
          onClick={book}
          disabled={!canBook}
          aria-busy={state === "sending" || plan.loading || undefined}
        >
          {state === "sending" ? (
            <>
              <Icon name="ri-loader-4-line" className="mj-trip-book__spin" aria-hidden="true" />
              {t("bucket.adding")}
            </>
          ) : state === "sent" ? (
            <>
              <Icon name="ri-check-line" aria-hidden="true" />
              {t("bucket.added")}
            </>
          ) : (
            <>
              <Icon name="ri-shopping-bag-3-line" aria-hidden="true" />
              {t("trip.book.button", {
                count: plan.loading || count === 0 ? plan.activities.length : count,
              })}
            </>
          )}
        </button>
      )}

      {plan.activities.length > 0 && past && (
        <p className="mj-trip-book__note mj-trip-book__note--warn">{t("trip.book.past")}</p>
      )}

      {state === "failed" && (
        <p className="mj-trip-book__note mj-trip-book__note--warn mj-fade-in" role="alert">
          {t("trip.book.failed")}
        </p>
      )}

      <div className="mj-trip-book__notes">
        {plan.activities.length > 0 && <p className="mj-trip-book__note">{t("trip.book.confirm_note")}</p>}
        {plan.activities.length > 0 && !plan.exact && !plan.loading && (
          <p className="mj-trip-book__note">{t("trip.book.exact_note")}</p>
        )}
        {plan.adultsOnly.map(({ item, minAge }) => (
          <p key={item.id} className="mj-trip-book__note">
            {minAge !== null
              ? t("trip.book.adults_only", { name: item.title, age: minAge })
              : t("trip.book.adults_only_some", { name: item.title })}
          </p>
        ))}
        {plan.tables.length > 0 && (
          <p className="mj-trip-book__note">
            {t("trip.book.tables_note", { count: plan.tables.length, names: names(plan.tables) })}
          </p>
        )}
        {plan.stays.length > 0 && (
          <p className="mj-trip-book__note">
            {t("trip.book.stays_note", { count: plan.stays.length, names: names(plan.stays) })}
          </p>
        )}
      </div>

      {plan.activities.length > 0 && (
        <button type="button" className="mj-trip-book__change" onClick={onChangeWhen}>
          <Icon name="ri-calendar-event-line" aria-hidden="true" />
          {t("trip.book.change")}
        </button>
      )}
    </div>
  );
}
